import React, { useState, useEffect, useCallback } from "react";
import api from "../../../api/rbacApi";
import LogFilters, { DEFAULT_FILTERS } from "./LogFilters";
import LogTable from "./LogTable";
import LogDetails from "./LogDetails";

function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_FILTERS });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  const [copied, setCopied] = useState(false);

  const fetchLogs = useCallback(async (pageNo = 1, currentFilters = DEFAULT_FILTERS) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: pageNo,
        limit: currentFilters.limit,
      };

      if (currentFilters.action) params.action = currentFilters.action.trim();
      if (currentFilters.userId) params.userId = currentFilters.userId.trim();
      if (currentFilters.bookingId) params.bookingId = currentFilters.bookingId.trim();
      if (currentFilters.ip) params.ip = currentFilters.ip.trim();
      if (currentFilters.path) params.path = currentFilters.path.trim();
      if (currentFilters.q) params.q = currentFilters.q.trim();
      if (currentFilters.fromDate) params.fromDate = currentFilters.fromDate;
      if (currentFilters.toDate) params.toDate = currentFilters.toDate;

      const response = await api.get("/api/dashboard/admin/http-audit-logs", { params });
      
      if (response.data?.success) {
        setLogs(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else {
          setPagination({
            page: pageNo,
            limit: Number(currentFilters.limit),
            total: (response.data.data || []).length,
            totalPages: 1,
          });
        }
      } else {
        setError(response.data?.message || "Failed to load audit logs.");
        setLogs([]);
      }
    } catch (err) {
      console.error("Error loading http audit logs:", err);
      setError(
        err.response?.data?.message || 
        err.message || 
        "An unexpected error occurred while fetching audit logs."
      );
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1, appliedFilters);
  }, [fetchLogs, appliedFilters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setAppliedFilters({ ...DEFAULT_FILTERS });
  };

  const handlePageChange = (pageNo) => {
    if (pageNo >= 1 && pageNo <= pagination.totalPages) {
      fetchLogs(pageNo, appliedFilters);
    }
  };

  const handleCopy = () => {
    if (!selectedLog) return;
    navigator.clipboard.writeText(JSON.stringify(selectedLog, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">

      <div className="row mb-3">
        <div className="col-12">
          <h3 className="fw-bold mb-1">HTTP Audit Logs Monitor</h3>
        </div>
      </div>

      <LogFilters
        filters={filters}
        loading={loading}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      {error && (
        <div className="alert alert-warning border-0 shadow-sm rounded-3 p-3 mb-4 d-flex align-items-start gap-3" role="alert">
          <i className="bi bi-exclamation-triangle-fill text-warning fs-4"></i>
          <div>
            <h6 className="alert-heading fw-bold mb-1">Logs Service Alert</h6>
            <p className="mb-0 small text-muted">{error}</p>
          </div>
        </div>
      )}

      <div className="row g-3">
        {/* Table View (Left side or full-screen) */}
        <div className={selectedLog ? "col-xl-8 col-lg-7 col-12" : "col-12"}>
          <LogTable
            logs={logs}
            loading={loading}
            selectedLog={selectedLog}
            onSelectLog={setSelectedLog}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>

        {selectedLog && (
          <div className="col-xl-4 col-lg-5 col-12">
            <LogDetails
              selectedLog={selectedLog}
              onClose={() => setSelectedLog(null)}
              onCopy={handleCopy}
              copied={copied}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Logs;